/**
 * ledger — the data behind the metered Usage page's ledger: the developer
 * report for the picked range, the daily limits, the members a limit can be
 * set for, activation, spend per day, one developer's days, and the Agents'
 * report. The same `/api/yagni-code-usage` endpoints the report has always
 * read; nothing here changes what is counted.
 *
 * Each read fails on its own: a failed limits read costs the limit column, a
 * failed day read costs the Spend per day fold, never the developer table.
 */
import { useCallback, useEffect, useState } from "react";
import type {
  YagniCodeActivationMember,
  YagniCodeActivationResponse,
  YagniCodeDailyUsagePoint,
  YagniCodeDailyUsageResponse,
  YagniCodeDeveloperBudget,
  YagniCodeDeveloperBudgetsResponse,
  YagniCodeUsageCell,
  YagniCodeUsageDeveloperRow,
  YagniCodeUsageReport,
  YagniCodeWorkerUsageReport,
  YagniCodeWorkspaceDailyDay,
  YagniCodeWorkspaceDailyResponse,
} from "@yagni/shared";

import { UsageAccessError, formatUsd, type UsageRange } from "./usageReport.js";

const DAY_MS = 24 * 60 * 60 * 1000;

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export type RangePreset = "this-month" | "last-30" | "last-90";

export const RANGE_PRESETS: ReadonlyArray<{ key: RangePreset; label: string }> = [
  { key: "this-month", label: "This month" },
  { key: "last-30", label: "Last 30 days" },
  { key: "last-90", label: "Last 90 days" },
];

/** A preset as a UTC range; `to` is exclusive (tomorrow). */
export function presetRange(preset: RangePreset, now: Date = new Date()): UsageRange {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const tomorrow = new Date(today.getTime() + DAY_MS);
  if (preset === "this-month") {
    return { from: isoDay(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))), to: isoDay(tomorrow) };
  }
  const days = preset === "last-30" ? 30 : 90;
  return { from: isoDay(new Date(today.getTime() - (days - 1) * DAY_MS)), to: isoDay(tomorrow) };
}

/** A picked "from … through" (both inclusive) as the API's range, or why not. */
export function customRange(from: string, through: string): UsageRange | string {
  if (!from || !through) return "Pick both dates.";
  if (from > through) return "The start date must not be after the end date.";
  return { from, to: isoDay(new Date(Date.parse(`${through}T00:00:00.000Z`) + DAY_MS)) };
}

const LANE_LABELS: Record<string, string> = {
  efficient: "Efficient",
  standard: "Standard",
  advanced: "Advanced",
  peak: "Peak",
  // Rows from before a balanced turn recorded its resolved tier.
  balanced: "Balanced",
};

export function laneLabel(lane: string): string {
  return LANE_LABELS[lane] ?? lane;
}

/** True when none of a row's lanes carry a Claude list price: its Claude
 * cells print a dash, never "$0.00". A row with no lanes at all spent
 * nothing, and $0.00 is honest for it. */
export function hasNoClaudePrice(lanes: readonly YagniCodeUsageCell[]): boolean {
  return lanes.length > 0 && lanes.every((lane) => lane.counterfactualMillicents === null);
}

/** "Unlimited" means no cap was ever set, which is not the same as a large one. */
export function formatLimitCell(budget: YagniCodeDeveloperBudget | undefined): string {
  if (!budget) return "Unlimited";
  return budget.overLimit ? `${formatUsd(budget.dailyLimitMillicents)} · reached` : formatUsd(budget.dailyLimitMillicents);
}

/** A typed dollar amount as millicents, or null: never a silent cap of 0 from a typo. */
export function parseUsd(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, "").replace(/,/g, "");
  // Two decimals at most, because the cell prints two.
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const dollars = Number.parseFloat(trimmed);
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100_000);
}

export interface WorkspaceMemberOption {
  userId: string;
  displayName: string | null;
  email: string | null;
}

/**
 * The developer table's rows: everyone with spend in the range, then anyone
 * with a saved cap but no spend in it (so a cap never goes invisible on a
 * quiet range), then members opened to be capped before their first dollar.
 * Workspace totals come from the report, never from these rows.
 */
export function withCappedDevelopers(
  developers: YagniCodeUsageDeveloperRow[],
  budgets: YagniCodeDeveloperBudget[] | null,
  pending: WorkspaceMemberOption[] = [],
): YagniCodeUsageDeveloperRow[] {
  const present = new Set(developers.map((row) => row.userId).filter((id): id is string => !!id));
  const extra: YagniCodeUsageDeveloperRow[] = [];
  const add = (userId: string, displayName: string | null, email: string | null, formerMember: boolean) => {
    if (present.has(userId)) return;
    present.add(userId);
    extra.push({
      userId,
      displayName,
      email,
      formerMember,
      lanes: [],
      totalSellMillicents: 0,
      totalDispatches: 0,
      totalCounterfactualMillicents: 0,
      totalSavingsMillicents: 0,
      totalParkedFrontierMillicents: 0,
      totalParkedSavingsMillicents: 0,
      counterfactualIncomplete: false,
    });
  };
  for (const budget of budgets ?? []) {
    add(budget.userId, budget.displayName, budget.email, budget.displayName === null && budget.email === null);
  }
  for (const member of pending) add(member.userId, member.displayName, member.email, false);
  return extra.length === 0 ? developers : [...developers, ...extra];
}

/** Members with no row yet: the people a new limit can be set for. */
export function cappableMembers(
  members: WorkspaceMemberOption[],
  rows: YagniCodeUsageDeveloperRow[],
): WorkspaceMemberOption[] {
  const shown = new Set(rows.map((row) => row.userId).filter((id): id is string => !!id));
  return members.filter((member) => !shown.has(member.userId));
}

/** What a member's tokens say, in words. Active is a week of use. */
export function activationStatus(
  member: YagniCodeActivationMember,
  now: Date,
): { label: string; tone: "active" | "quiet" | "never" } {
  if (!member.hasToken) return { label: "Never logged in", tone: "never" };
  if (member.lastUsedAt === null) return { label: "Logged in, not used yet", tone: "quiet" };
  const lastUsed = new Date(member.lastUsedAt);
  if (now.getTime() - lastUsed.getTime() <= 7 * DAY_MS) return { label: "Active", tone: "active" };
  return { label: `Quiet since ${isoDay(lastUsed)}`, tone: "quiet" };
}

export const CLIENT_LABELS: Record<string, string> = {
  cli: "CLI",
  desktop: "Desktop",
  unknown: "Unknown app",
};

/** Every day in [from, to), gap days as explicit zeroes, so a quiet weekend shows. */
export function fillWorkspaceDailySeries(
  from: string,
  to: string,
  days: YagniCodeWorkspaceDailyDay[],
): YagniCodeWorkspaceDailyDay[] {
  const byDay = new Map(days.map((day) => [day.day, day]));
  const filled: YagniCodeWorkspaceDailyDay[] = [];
  const end = Date.parse(`${to}T00:00:00.000Z`);
  for (let t = Date.parse(`${from}T00:00:00.000Z`); t < end && filled.length <= 366; t += DAY_MS) {
    const day = isoDay(new Date(t));
    filled.push(
      byDay.get(day) ?? {
        day,
        lanes: [],
        totalSellMillicents: 0,
        totalDispatches: 0,
        totalCounterfactualMillicents: 0,
        totalSavingsMillicents: 0,
        totalParkedFrontierMillicents: 0,
        totalParkedSavingsMillicents: 0,
        counterfactualIncomplete: false,
        unbilledSellMillicents: 0,
      },
    );
  }
  return filled;
}

export function fillDailySeries(
  from: string,
  to: string,
  days: YagniCodeDailyUsagePoint[],
): YagniCodeDailyUsagePoint[] {
  const byDay = new Map(days.map((point) => [point.day, point]));
  const filled: YagniCodeDailyUsagePoint[] = [];
  const end = Date.parse(`${to}T00:00:00.000Z`);
  for (let t = Date.parse(`${from}T00:00:00.000Z`); t < end && filled.length <= 366; t += DAY_MS) {
    const day = isoDay(new Date(t));
    filled.push(
      byDay.get(day) ?? { day, sellMillicents: 0, counterfactualMillicents: 0, counterfactualIncomplete: false, dispatches: 0 },
    );
  }
  return filled;
}

/** "Oct 6" for "2026-10-06", in UTC. */
export function shortDay(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function rangeQuery(range: UsageRange): string {
  return `from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
}

async function getJson<T>(url: string, what: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 403) throw new UsageAccessError();
  if (!res.ok) throw new Error(`Failed to load ${what} (${res.status})`);
  return (await res.json()) as T;
}

export function usageCsvHref(range: UsageRange): string {
  return `/api/yagni-code-usage?${rangeQuery(range)}&format=csv`;
}

export async function fetchDeveloperDaily(userId: string, range: UsageRange): Promise<YagniCodeDailyUsageResponse> {
  return getJson(`/api/yagni-code-usage/developers/${encodeURIComponent(userId)}/daily?${rangeQuery(range)}`, "daily usage");
}

async function saveBudget(userId: string, dailyLimitMillicents: number | null): Promise<YagniCodeDeveloperBudget[]> {
  const res = await fetch(`/api/yagni-code-usage/budgets/${encodeURIComponent(userId)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dailyLimitMillicents }),
  });
  if (res.status === 403) throw new UsageAccessError();
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Failed to save the limit (${res.status})`);
  }
  return ((await res.json()) as { budgets: YagniCodeDeveloperBudget[] }).budgets;
}

/** One async read keyed on a value: data, error, and whether it is in flight. */
function useRead<T>(key: string, read: () => Promise<T>) {
  const [state, setState] = useState<{ data: T | null; error: unknown; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });
  useEffect(() => {
    let cancelled = false;
    setState({ data: null, error: null, loading: true });
    read()
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ data: null, error, loading: false });
      });
    return () => {
      cancelled = true;
    };
    // `read` is rebuilt every render; the key says when to read again.
  }, [key]);
  return state;
}

function message(error: unknown, fallback: string): string | null {
  if (error === null) return null;
  return error instanceof Error ? error.message : fallback;
}

export interface Ledger {
  report: YagniCodeUsageReport | null;
  reportState: "loading" | "ready" | "error" | "forbidden";
  reportError: string | null;
  /** Null while unknown: never an editable "Unlimited" over a cap nobody can see. */
  budgets: YagniCodeDeveloperBudget[] | null;
  enforced: boolean;
  budgetError: string | null;
  saveLimit: (userId: string, next: number | null) => Promise<boolean>;
  savingUserId: string | null;
  members: WorkspaceMemberOption[] | null;
  activation: YagniCodeActivationMember[] | null;
  activationError: string | null;
  daily: YagniCodeWorkspaceDailyResponse | null;
  dailyError: string | null;
  workers: YagniCodeWorkerUsageReport | null;
  workersError: string | null;
}

export function useLedger(range: UsageRange): Ledger {
  const key = rangeQuery(range);
  const report = useRead(key, () =>
    getJson<{ usage: YagniCodeUsageReport }>(`/api/yagni-code-usage?${key}`, "usage").then((data) => data.usage),
  );
  const daily = useRead(key, () => getJson<YagniCodeWorkspaceDailyResponse>(`/api/yagni-code-usage/daily?${key}`, "daily usage"));
  const workers = useRead(key, () =>
    getJson<{ workers: YagniCodeWorkerUsageReport }>(`/api/yagni-code-usage/workers?${key}`, "Agent usage").then(
      (data) => data.workers,
    ),
  );
  // Range-independent: a cap belongs to the person, activation to their tokens.
  const activation = useRead("once", () =>
    getJson<YagniCodeActivationResponse>("/api/yagni-code-usage/activation", "activation").then((data) => data.members),
  );
  const members = useRead("once", () =>
    getJson<{ members: WorkspaceMemberOption[] }>("/api/members", "members").then((data) =>
      data.members.map((m) => ({ userId: m.userId, displayName: m.displayName ?? null, email: m.email ?? null })),
    ),
  );
  const budgetsRead = useRead("once", () =>
    getJson<YagniCodeDeveloperBudgetsResponse>("/api/yagni-code-usage/budgets", "limits"),
  );

  const [budgets, setBudgets] = useState<YagniCodeDeveloperBudget[] | null>(null);
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  useEffect(() => {
    if (budgetsRead.data) setBudgets(budgetsRead.data.budgets);
    if (budgetsRead.error) setBudgetError(message(budgetsRead.error, "Failed to load limits"));
  }, [budgetsRead.data, budgetsRead.error]);

  const saveLimit = useCallback(async (userId: string, next: number | null) => {
    setSavingUserId(userId);
    setBudgetError(null);
    try {
      setBudgets(await saveBudget(userId, next));
      return true;
    } catch (err) {
      setBudgetError(err instanceof Error ? err.message : "Failed to save the limit");
      return false;
    } finally {
      setSavingUserId(null);
    }
  }, []);

  const reportState = report.loading
    ? "loading"
    : report.error instanceof UsageAccessError
      ? "forbidden"
      : report.error
        ? "error"
        : "ready";

  return {
    report: report.data,
    reportState,
    reportError: message(report.error, "Failed to load usage"),
    budgets,
    enforced: budgetsRead.data?.enforced ?? false,
    budgetError,
    saveLimit,
    savingUserId,
    members: members.data,
    activation: activation.data,
    activationError: message(activation.error, "Failed to load activation"),
    daily: daily.data,
    dailyError: message(daily.error, "Failed to load daily usage"),
    workers: workers.data,
    workersError: message(workers.error, "Failed to load Agent usage"),
  };
}
